import type { Card, PortalWait, Preflight, QuestionHow, UnansweredQuestion } from './api';
import { ageOf, until, whenLocal } from './time';

/**
 * What a card looks like in the queue: its badge and its colour.
 *
 * The colour is semantic and means the same thing on every screen — teal is
 * alive/send, rust is stop/stuck, amber is held/veto. A card type gets a hue
 * because of what approving it DOES, not to make the list colourful.
 */
export type Tone = 'reply' | 'signup' | 'rescue' | 'hold' | 'neutral';

export interface Look { label: string; tone: Tone }

const BY_TYPE: Record<string, Look> = {
  email_draft: { label: 'Reply', tone: 'reply' },
  setup_rescue: { label: 'Rescue', tone: 'rescue' },
  approve_signup: { label: 'Signup', tone: 'signup' },
  fraud_suspend: { label: 'Fraud', tone: 'hold' },
  suspend_account: { label: 'Suspend', tone: 'hold' },
  cancel_account: { label: 'Cancel', tone: 'hold' },
  reactivate_account: { label: 'Reactivate', tone: 'reply' },
  balance_negative: { label: 'Balance', tone: 'hold' },
  paying_customer_left: { label: 'Left us', tone: 'hold' },
  unanswered_email: { label: 'Unanswered', tone: 'neutral' },
  sales_lead: { label: 'Sales lead', tone: 'signup' }, // fix-67: a prospect to call
  customer_called: { label: 'Customer called', tone: 'signup' }, // fix-81: a customer who called our sales line
  drafter_edits: { label: 'Your edits', tone: 'neutral' }, // fix-73: the week's edits to drafts, by kind
  hero_ticket: { label: 'To do', tone: 'neutral' }, // hero-tickets-1: Hero updated a ticket we logged; Done sends nothing
  generic: { label: 'Note', tone: 'neutral' },
};

/** A send_template card is named by what the send is for, not by "template". */
const BY_CLASS: Record<string, Look> = {
  trial_checkin: { label: 'Trial check-in', tone: 'reply' },
  trial_extension: { label: 'Extension', tone: 'hold' },
  topup_reminder: { label: 'Top-up', tone: 'hold' },
  routine_nudge: { label: 'Nudge', tone: 'neutral' },
  consequential: { label: 'Closure', tone: 'hold' },
  port_admin: { label: 'Port admin', tone: 'neutral' },
};

export function cardLook(card: Pick<Card, 'type' | 'class'>): Look {
  if (card.type === 'send_template') {
    return BY_CLASS[card.class] ?? { label: 'Template', tone: 'neutral' };
  }
  return BY_TYPE[card.type] ?? { label: card.type.replace(/_/g, ' '), tone: 'neutral' };
}

/**
 * Consequential cards are ALWAYS held for a person, whatever the policy mode
 * says, and the queue says so on the card rather than in a footnote. T31
 * suspends an account on send; the operator should not have to remember which
 * ones those are.
 */
export function isConsequential(card: Pick<Card, 'class' | 'type'>): boolean {
  return card.class === 'consequential' || DESTRUCTIVE_TYPES.has(card.type);
}

/**
 * The types whose approval needs a typed confirmation in this UI.
 *
 * The same list the desk enforces as role=operator (lib/cards.js). It is
 * repeated here NOT as a security control — the desk decides, and a browser
 * cannot be trusted to — but so the operator is asked before the request is
 * sent rather than told afterwards that it worked.
 */
// The cards whose body a person writes in (lib/body-check.js WRITTEN_BODY_TYPES in the PSA repo): the ones with a
// draft to edit, and so the ones focus (fix-74) applies to.
const WRITTEN = new Set(['email_draft', 'setup_rescue']);
export const hasDraft = (card: Pick<Card, 'type' | 'payload'>) => WRITTEN.has(card.type) && Boolean(card.payload.body);

export const DESTRUCTIVE_TYPES = new Set([
  'cancel_account', 'suspend_account', 'approve_signup', 'fraud_suspend',
  'reactivate_account',
]);

/** The person, the account, the one line under the title. */
export function who(card: Pick<Card, 'customer_name' | 'account_no'>): string {
  const name = card.customer_name?.trim();
  if (name && card.account_no) return `${name} · ${card.account_no}`;
  if (name) return name;
  if (card.account_no) return String(card.account_no);
  // Card 971 is this: a reply to someone attribution never matched to an
  // account. Saying so is the point — it is why pre-flight will refuse it.
  return 'no account matched';
}

/**
 * Who decided this card, in words rather than in actor strings.
 *
 * The Done-today view has to distinguish three things that look alike in the
 * database and are not alike at all: the operator decided, a veto window
 * expired with nobody objecting, and the one automatic action fired.
 */
export function decidedBy(actor: string | null, me: string | null): string {
  if (!actor) return '—';
  if (actor === 'auto-veto-expired') return 'veto expired, nobody objected';
  if (actor === 'auto:fraud_sweep') return 'the fraud sweep, automatically';
  if (actor.startsWith('auto:')) return `automatically (${actor.slice(5)})`;
  if (me && actor.toLowerCase() === me.toLowerCase()) return 'you';
  return actor;
}

/**
 * WHAT PRESSING APPROVE SHOULD DO, given what pre-flight says.
 *
 * soak-3 #4+5. The screen used to offer "Try it anyway" on EVERY refusal, and
 * for most of them it could not work:
 *
 *   - a PERMANENT refusal (the customer paid, called, wrote in; the rules would
 *     not choose this account today) can never pass. The desk now expires the
 *     card instead of failing it, so "trying anyway" just throws it away with a
 *     worse word on it.
 *   - `dead` IS overridable — the desk acts on a closed account given a written
 *     reason — but the button never asked for one and sent none, so it was
 *     refused again every time. The one override that exists was unreachable.
 *   - anything else (a placeholder still unfilled, a card type with no spec) is
 *     fixed somewhere other than this button: edit the card, or deploy.
 *
 * So a button is only offered where pressing it can succeed.
 */
export type ApproveAction =
  | { kind: 'send' }
  | { kind: 'dismiss'; reason: string }
  | { kind: 'override'; reason: string }
  | { kind: 'explain'; reason: string };

export function approveAction(pf: Pick<Preflight, 'ok' | 'permanent' | 'signal' | 'reason' | 'next'> | null | undefined): ApproveAction {
  // No verdict yet, or a pass: approve as normal. A still-loading strip must
  // not block the operator — the desk runs every gate again on approval.
  if (!pf || pf.ok !== false) return { kind: 'send' };

  const why = [pf.reason, pf.next].filter(Boolean).join(' ').trim() || 'Pre-flight refuses this card.';

  // Checked first and explicitly `=== true`: a desk older than soak-3 sends no
  // `permanent` at all, and undefined must not be read as final.
  if (pf.permanent === true) return { kind: 'dismiss', reason: why };
  if (pf.signal === 'dead') return { kind: 'override', reason: why };
  return { kind: 'explain', reason: why };
}

/**
 * Which buttons a pending signup card offers (fix-15). The desk decides the
 * verdict from its hard/soft table: a rejected signup is answered by Confirm
 * rejection or Override and activate, a review by Approve or Reject, and a
 * clean one as before. Anything that is not a signup, or a signup raised before
 * fix-15, keeps the usual buttons.
 */
export type SignupButtons = 'rejected' | 'review' | 'usual';

export function signupButtons(card: Pick<Card, 'type' | 'payload'>): SignupButtons {
  if (card.type !== 'approve_signup') return 'usual';
  if (card.payload.verdict === 'rejected') return 'rejected';
  if (card.payload.verdict === 'review') return 'review';
  return 'usual';
}

/**
 * fix-75 / #217: an approved card the portal could not take yet. Only while it is approved — once it runs, fails or is
 * stopped the old wait is history, not a state.
 */
export function portalWait(card: Pick<Card, 'status' | 'payload'>): PortalWait | null {
  const w = card.payload?.portal_wait;
  return card.status === 'approved' && w && typeof w.since === 'string' ? w : null;
}

/** "Waiting for the portal since Mon 05:32 · tries every 10 minutes until Tue 05:32 (23h 10m) · 3 tries, last 4m ago" */
export function waitLine(w: PortalWait, now: number = Date.now()): string {
  return `Waiting for the portal since ${whenLocal(w.since)} · tries every 10 minutes until ${whenLocal(w.until)} `
    + `(${until(w.until, now)}) · ${w.tries} ${w.tries === 1 ? 'try' : 'tries'}, last ${ageOf(w.last_try, now)} ago`;
}

/** A draft's questions with no answer yet: Approve asks before sending without them (ADR-0343, PSA #305). */
export const waitingQuestions = (card: Pick<Card, 'payload'>): UnansweredQuestion[] =>
  (card.payload.unanswered_questions ?? []).filter((q) => !q.answer?.trim());

/** One customer question and the gaps that are part of it; `n` null is Other, the gaps tied to no question. Each gap
 * keeps `i`, its place in unanswered_questions, which answering and Slack's boxes go by. */
export interface GapGroup { n: number | null; question: string | null; how: QuestionHow | null; gaps: { q: UnansweredQuestion; i: number }[] }

/** partly-1 (PSA #312, DRAFT-68): the gaps under their question, in the customer's order, Other last; null when no gap
 * names a question — a card raised before partly-1 — and the panel lists them as before. */
export function gapGroups(card: Pick<Card, 'payload'>): GapGroup[] | null {
  const qs = card.payload.questions ?? [];
  const gaps = (card.payload.unanswered_questions ?? []).map((q, i) => ({ q, i }));
  const tied = (g: { q: UnansweredQuestion }) => g.q.of != null && Boolean(qs[g.q.of]);
  if (!gaps.some(tied)) return null;
  const other = gaps.filter((g) => !tied(g));
  return [...qs.map((x, k) => ({ n: k + 1, question: x.question, how: x.how, gaps: gaps.filter((g) => tied(g) && g.q.of === k) }))
    .filter((g) => g.gaps.length), ...(other.length ? [{ n: null, question: null, how: null, gaps: other }] : [])];
}

/** The card's QUESTIONS line, as the desk writes it: "2 from knowledge · 0 by inference · 6 partly or wholly left to
 * you (5 partly, 1 wholly)"; with no partly, "… · 1 left to you" as before partly-1. */
export function questionCounts(qs: { how: QuestionHow | null }[]): string {
  const n = (h: QuestionHow) => qs.filter((q) => q.how === h).length;
  return `${n('knowledge')} from knowledge · ${n('inference')} by inference · ${n('partly')
    ? `${n('partly') + n('operator')} partly or wholly left to you (${n('partly')} partly, ${n('operator')} wholly)`
    : `${n('operator')} left to you`}`;
}
